import { AxiosError, AxiosResponse } from 'axios'
import { type AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import {
  RolodexEntryDispenserType,
  useDispenseRolodexProperAndCacheEntries,
} from 'features/rolodex/rolodexEntryDispenser'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import compact from 'lodash/compact'
import every from 'lodash/every'
import React from 'react'
import useSWR, { SWRConfiguration } from 'swr'
import {
  RolodexAdvancedFilterType,
  RolodexEntryPatchJson,
  RolodexEntryTagsType,
  RolodexEntryType,
  RolodexFilterType,
  RolodexHistogramFilter,
  RolodexLoadedEntryJson,
} from 'types'

export interface RolodexEntryQueryBody {
  /** A list of entry IDs to filter down to */
  ids?: string[]
  /** The entry type or types to filter down to */
  type?: RolodexEntryType | RolodexEntryType[]
  /** A set of tags for filtering (returned entries must include all provided tags) */
  tags?: RolodexEntryTagsType | Record<string, string[]>
  /** The inverse of `tags`, tag values included here will be excluded. If a tag has an empty string value, all entries with that tag will be excluded */
  exclude_tags?: RolodexEntryTagsType | Record<string, string[]>
  /** Any filters to apply to this query */
  filters?: RolodexFilterType[] | RolodexHistogramFilter[]
  /** What to order entries in the main query by (for example, name) */
  order_by?: Record<string, unknown>[]
  /** The max number of entries to return in the main query (excluding links) */
  limit?: number
  /** How many matching entries to skip before applying `limit`. Ignored by load/ids and load/count. */
  offset?: number
  /** A list of types to grab for each entry in the main query (units per component) */
  links?: RolodexEntryType[]
  /** Specify an advanced filter to constrain which entries should be loaded. This allows a combination of multiple filters to be combined with logical operators. */
  advanced_filter?: RolodexAdvancedFilterType
}

export const DEFAULT_ROLODEX_ENTRY_LIMIT = 10000

/** The SWR key part for one query body. Hooks that pass the same body share one cache entry. */
export function serializeRolodexEntryQuery(
  body: RolodexEntryQueryBody
): string {
  return JSON.stringify({
    ...body,
    limit: body.limit || (body.ids?.length ?? DEFAULT_ROLODEX_ENTRY_LIMIT),
    tags: { ...body.tags },
  })
}

/** Loads each serialized query body. A response that is not a list becomes an empty list. */
export function loadRolodexEntryQueries(
  axios: AuthedAxiosRequests,
  apiURL: string,
  bodies: readonly string[]
): Promise<RolodexLoadedEntryJson[][]> {
  return Promise.all(
    bodies.map(async (body) => {
      const response: AxiosResponse = await axios.post(
        `${apiURL}/api/v2/entries/load`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      )
      const result = response.data
      if (!Array.isArray(result)) {
        console.error('Rolodex entry fetch failed', JSON.parse(body), result)
        return []
      }
      return result
    })
  )
}

/**
 * A hook for fetching Rolodex entries from a provided query body or array of
 * query bodies. Uses SWR behind the scenes to avoid unnecessary refreshes
 * and produces neatly-formatted return values and utility methods.
 * @param queryOrQueries  The query body, or an array of bodies, or undefined
 * @param options         Options to customize how Rolodex entries are queried
 */
export const useRolodexEntries = (
  queryOrQueries:
    | RolodexEntryQueryBody
    | ReadonlyArray<RolodexEntryQueryBody>
    | undefined,
  options?: {
    /** Whether to return locally-cached entry JSON objects (defaults to true) */
    allowCache?: boolean
    swrOptions?: SWRConfiguration
  }
): RolodexEntryDispenserType & {
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** To account for the initial case before the query fires  */
  hasEverLoaded: boolean
  /** Any errors returned from SWR */
  error: AxiosError
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
} => {
  //----------------------------------------------------------------------------
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL, entryCache } = React.useContext(RolodexContext)

  const queryBodiesStr = React.useMemo(() => {
    if (!queryOrQueries || !isAuthenticated) return '[]'
    const arr: ReadonlyArray<RolodexEntryQueryBody> = Array.isArray(
      queryOrQueries
    )
      ? queryOrQueries
      : [queryOrQueries]
    const queryBodiesJSON = arr.map(serializeRolodexEntryQuery)
    return JSON.stringify(queryBodiesJSON) // Allows this to be used in subsequent dep-arrays, even if queryOrQueries is not memo'd
  }, [queryOrQueries, isAuthenticated])

  // Doing this in 2 steps ensures that the caller doesn't have to memoize a `queryOrQueries` object
  const queryBodies: string[] = React.useMemo(
    () => JSON.parse(queryBodiesStr),
    [queryBodiesStr]
  )

  // ----------------------------------------------------------------------------
  // Rolodex "Proper" Fetch

  const fetcher = React.useCallback(
    (keys: string[]) => loadRolodexEntryQueries(axios, apiURL, keys),
    [apiURL, axios]
  )

  const {
    data: rolodexProperFetchData,
    isLoading,
    isValidating,
    error: swrError,
    mutate,
  } = useSWR<RolodexLoadedEntryJson[][]>(
    queryBodies,
    fetcher,
    options?.swrOptions
  )

  // ----------------------------------------------------------------------------
  // Rolodex-Cache "fetch"

  // The Rolodex-Cache options that meet the query criteria (if allowCache)
  const rolodexCacheFetchData = React.useMemo(() => {
    if (!(options?.allowCache ?? true)) return undefined

    const optionalCache: Map<string, RolodexEntryPatchJson | 'deleted'> =
      new Map()
    const foundLinks: Set<string> = new Set()

    entryCache.forEach((patch, id) => {
      if (patch === 'deleted') {
        optionalCache.set(id, patch)
        return
      }

      // ensure that the entry is valid for any `queryBodies`
      // TODO ENT-1038: Implement filters
      if (
        queryBodies.some((body) => {
          // Ids check
          const bodyJSON = JSON.parse(body)
          if (bodyJSON.ids && !bodyJSON.ids.includes(id)) {
            return false
          }
          // Type check
          if (
            (bodyJSON.type && Array.isArray(bodyJSON.type)
              ? !!bodyJSON.type.length
              : true) &&
            patch.type
          ) {
            const types: string[] = Array.isArray(bodyJSON.type)
              ? bodyJSON.type
              : compact([bodyJSON.type])
            if (!types.includes(patch.type)) {
              return false
            }
            // tag check
            if (
              bodyJSON.tags &&
              !every(Object.entries(bodyJSON.tags), ([key, value]) => {
                const keyAsRolodexType = key as RolodexEntryType // Object.entries returns string keys
                if (Array.isArray(value)) {
                  return value.includes(patch?.tags?.[keyAsRolodexType])
                }

                return patch?.tags?.[keyAsRolodexType] === value
              })
            ) {
              return false
            }
          }
          return true
        })
      ) {
        // if there is a query body that fits the entryCache patch entry, add it to optionalCache
        optionalCache.set(id, patch)
        const links = compact(
          queryBodies.flatMap((query) => JSON.parse(query).links)
        )
        if (links && links.length > 0) {
          links.forEach((linkType: RolodexEntryType) => {
            const linkedId = patch.tags?.[linkType]
            if (linkedId && !foundLinks.has(linkedId)) {
              const linkedEntry = entryCache.get(linkedId)
              if (linkedEntry && linkedEntry !== 'deleted') {
                optionalCache.set(linkedId, linkedEntry)
                foundLinks.add(linkedId)
              }
            }
          })
        }
      }
    })

    return optionalCache
  }, [entryCache, options?.allowCache, queryBodies])

  // Generate the Forecast-Classes for the fetched Rolodex entries
  // Also generate any derived-classes from the fetched rolodex entries (e.g. generate the CDFs from a CDS)
  const dispenser = useDispenseRolodexProperAndCacheEntries(
    rolodexProperFetchData,
    rolodexCacheFetchData
  )

  // ----------------------------------------------------------------------------
  // SWR Utility Method(s)
  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return {
    ...dispenser,
    isLoading: (!rolodexProperFetchData && queryBodies.length > 0) || isLoading,
    isValidating: isValidating,
    hasEverLoaded: !!rolodexProperFetchData,
    error: swrError,
    forceRefresh,
  }
}
